import React, { useEffect, useState } from 'react';
import { X, Calendar, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { Event } from '../../../services/EventService';
import { supabase } from '../../../config/supabase';

interface AddToCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventData: {
    id: string;
    title: string;
    description?: string;
    startDate: string;
    startTime: string;
    endTime?: string;
    location?: string;
  };
}

export const AddToCalendarModal = ({ 
  isOpen, 
  onClose, 
  eventData 
}: AddToCalendarModalProps) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState('');
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');


  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('avatar_url')
            .eq('id', user.id)
            .single();
          if (error) {
            console.error('Error fetching profile:', error);
          } else {
            setProfile(data?.avatar_url || '');
          }
        } catch (e) {
          console.error('Unexpected error fetching profile:', e);
        }
      }
    };
    fetchUserProfile();
  }, [user]);

  if (!isOpen) return null;

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Añadir helper antes de handleRegister
  async function ensureLoggedInUserAttendance(): Promise<any> {
    if (!user) return { error: 'No hay usuario', alreadyConfirmed: false };
    const userEmail = user.email || '';
    if (!userEmail) return { error: 'Falta el email del usuario', alreadyConfirmed: false };

    // Buscar registro existente (por email único en el evento)
    const { data: existing, error: existingErr } = await supabase
      .from('event_users')
      .select('id,rsvp_status,invitation_status,approval_status,is_on_waiting_list')
      .eq('event_id', eventData.id)
      .eq('email', userEmail)
      .maybeSingle();

    if (existingErr) return { error: existingErr, alreadyConfirmed: false };

    if (existing) {
      // Ya confirmado
      if (existing.rsvp_status === 'confirmed' && existing.invitation_status === 'accepted') {
        return { error: null, alreadyConfirmed: true };
      }
      // Actualizar a confirmado (evita violar unique constraint)
      const { error: updateErr } = await supabase
        .from('event_users')
        .update({
          rsvp_status: 'confirmed',
            invitation_status: 'accepted',
          user_id: user?.id ?? 'system',
          approval_status: existing.approval_status === 'pending' ? 'approved' : existing.approval_status,
          is_on_waiting_list: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id);
      return { error: updateErr, alreadyConfirmed: false };
    }

    // No existe -> insertar (addUserToEvent)
    const { error: insertErr} = await Event.addUserToEvent(
      eventData.id,
      {
        email: userEmail,
        name: user.user_metadata?.name || userEmail.split('@')[0],
        avatar_url: profile
      },
      user.id
    );
    const finalError: any = insertErr;
    // Si hubo condición de carrera y devuelve duplicate, reintentar vía update
    if (finalError && /duplicate key/i.test(finalError?.message || '')) {
      return ensureLoggedInUserAttendance();
    }

    return { error: finalError, alreadyConfirmed: false };
  }

  const handleRegister = async () => {
    setError('');
    if (isLoading) return;

    // USER LOGGED IN
    if (user) {
      setIsLoading(true);
      try {
        const { error: attendErr } = await ensureLoggedInUserAttendance();
        if (attendErr) {
          setError('No se pudo registrar. Intenta nuevamente.');
        } else {
          setStep('success');
        }
      } catch (e) {
        console.error(e);
        setError('Error inesperado. Intenta nuevamente.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // USER NO LOGUEADO -> invitación
    if (!email.trim()) {
      setError('El email es obligatorio');
      return;
    }
    if (!validateEmail(email)) {
      setError('Por favor ingresa un email válido');
      return;
    }

    setIsLoading(true);
    try {
      const { error: inviteErr } = await Event.inviteUser(
        eventData.id,
        { email: email.trim(), name: email.trim().split('@')[0] },
        'system'
      );
      if (inviteErr) {
        setError('No se pudo enviar la invitación. Intenta nuevamente.');
      } else {
        setStep('success');
      }
    } catch (e) {
      console.error(e);
      setError('Error inesperado. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCalendar = (type: 'google' | 'outlook' | 'ics') => {
    const { title, description, startDate, startTime, endTime, location } = eventData;
    const startDateTime = new Date(`${startDate}T${startTime}:00`);
    const endDateTime = endTime ? new Date(`${startDate}T${endTime}:00`) : new Date(startDateTime.getTime() + 60 * 60 * 1000);
    
    if (endDateTime < startDateTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    switch (type) {
      case 'google':
        const formatGoogleDate = (date: Date) => {
          return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };
        const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formatGoogleDate(startDateTime)}/${formatGoogleDate(endDateTime)}&details=${encodeURIComponent(description || '')}&location=${encodeURIComponent(location || '')}`;
        window.open(googleUrl, '_blank');
        break;
      
      case 'outlook':
        const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdt=${startDateTime.toISOString()}&enddt=${endDateTime.toISOString()}&body=${encodeURIComponent(description || '')}&location=${encodeURIComponent(location || '')}`;
        window.open(outlookUrl, '_blank');
        break;
      
      case 'ics':
        downloadICS();
        break;
    }

    // Cerrar modal después de descargar
    setTimeout(() => {
      onClose();
    }, 500);
  };

  const downloadICS = () => {
    const { title, description, startDate, startTime, endTime, location } = eventData;
    const startDateTime = new Date(`${startDate}T${startTime}:00`);
    const endDateTime = endTime ? new Date(`${startDate}T${endTime}:00`) : new Date(startDateTime.getTime() + 60 * 60 * 1000);
    
    const formatICSDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Punta Tech Meetup//Calendario de Eventos//ES',
      'BEGIN:VEVENT',
      `UID:${eventData.id}@puntameetup.com`,
      `DTSTART:${formatICSDate(startDateTime)}`,
      `DTEND:${formatICSDate(endDateTime)}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${description || ''}`,
      `LOCATION:${location || ''}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    link.click();
  };

  const renderFormStep = () => (
    <>
      <div className="flex justify-between items-start mb-6">
        <div className="w-full">
          <h2 className="text-xl font-bold mb-2">RSVP</h2>
          <p className="text-sm text-gray-500">
            {user ? 'Sumate a este evento y agregalo a tu calendario' : 'Ingresá tu email para sumarte a este evento'}
          </p>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-4"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {!user && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email *
          </label>
          <input
            type="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            className={`w-full px-4 py-3 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors ${
              error ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={isLoading}
          />
        </div>
      )}

      {user && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Sesión iniciada como:</strong> {user.email}
          </p>
        </div>
      )}

      <button
        onClick={handleRegister}
        disabled={isLoading}
        className="w-full py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          'Asistir'
        )}
      </button>
    </>
  );

  const renderSuccessStep = () => (
    <>
      <div className="flex justify-between items-start mb-6">
        <div className="w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold mb-2">¡Estás registrado!</h2>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-4"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {!user && (
        <div className="mt-4">
          <p className="text-sm text-gray-600">
            Se envió un email de confirmación a {email}
          </p>
        </div>
      )}
    </>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        {step === 'form' ? renderFormStep() : renderSuccessStep()}
      </div>
    </div>
  );
};